import type { ComponentType, SVGProps } from 'react';
import {
  PhoneOutgoing, Phone, Voicemail, PhoneOff, AlertTriangle, Calendar, Clock, Ban, Circle, AlertCircle, ThumbsDown, CheckCircle2,
} from 'lucide-react';

/**
 * The one status map (D9, DESIGN.md §3). VFStatusPill, VFCallStatusPill and
 * StatusBadge read their colour and icon here (useLiveCallState joins with
 * the call page, W3); there
 * used to be seven palettes. Colours come from the --vf-status-* tokens,
 * whose text is AA on its own 8 % tint (designTokens.test.ts).
 *
 * Every status has an icon: colour never carries the meaning alone.
 */
export type StatusTone = 'ringing' | 'connected' | 'voicemail' | 'noanswer' | 'failed' | 'booked' | 'callback' | 'dnc' | 'neutral';

type Icon = ComponentType<SVGProps<SVGSVGElement>>;

export interface ToneStyle { fg: string; bg: string; bd: string }

const tokenFor: Record<StatusTone, string> = {
  ringing: '--vf-status-ringing-fg',
  connected: '--vf-status-connected-fg',
  voicemail: '--vf-status-voicemail-fg',
  noanswer: '--vf-status-noanswer-fg',
  failed: '--vf-status-failed-fg',
  booked: '--vf-status-booked-fg',
  callback: '--vf-status-callback-fg',
  dnc: '--vf-status-dnc-fg',
  neutral: '--vf-text-2',
};

export function toneStyle(tone: StatusTone): ToneStyle {
  const fg = `var(${tokenFor[tone]})`;
  return {
    fg,
    bg: `color-mix(in srgb, ${fg} 8%, transparent)`,
    bd: `color-mix(in srgb, ${fg} 20%, transparent)`,
  };
}

interface StatusEntry { tone: StatusTone; icon: Icon }

/** Every status the app displays, call states and outcomes alike. */
export const STATUS_MAP: Record<string, StatusEntry> = {
  // Not called yet / attempted, waiting (no D9 colour: neutral)
  new: { tone: 'neutral', icon: Circle },
  pending: { tone: 'neutral', icon: Circle },
  recall: { tone: 'neutral', icon: Clock },
  completed: { tone: 'neutral', icon: CheckCircle2 },
  not_interested: { tone: 'neutral', icon: ThumbsDown },
  // Ringing / dialling (blue)
  dialing: { tone: 'ringing', icon: PhoneOutgoing },
  ringing: { tone: 'ringing', icon: PhoneOutgoing },
  ai_analyzing: { tone: 'ringing', icon: PhoneOutgoing },
  verifying: { tone: 'ringing', icon: PhoneOutgoing },
  // On the line / reached a human (green)
  contacted: { tone: 'connected', icon: Phone },
  connected: { tone: 'connected', icon: Phone },
  human_detected: { tone: 'connected', icon: Phone },
  connected_to_agent: { tone: 'connected', icon: Phone },
  online: { tone: 'connected', icon: Phone },
  qualified: { tone: 'connected', icon: Phone },
  interested: { tone: 'connected', icon: Phone },
  // Voicemail (dark grey)
  voicemail: { tone: 'voicemail', icon: Voicemail },
  left_voicemail: { tone: 'voicemail', icon: Voicemail },
  // No answer (grey)
  no_answer: { tone: 'noanswer', icon: PhoneOff },
  busy: { tone: 'noanswer', icon: PhoneOff },
  canceled: { tone: 'noanswer', icon: PhoneOff },
  not_reachable: { tone: 'noanswer', icon: PhoneOff },
  ended: { tone: 'noanswer', icon: PhoneOff },
  hold: { tone: 'noanswer', icon: Clock },
  // Failed / invalid number (red)
  failed: { tone: 'failed', icon: AlertTriangle },
  wrong_number: { tone: 'failed', icon: AlertTriangle },
  no_number: { tone: 'failed', icon: AlertCircle },
  ios_filter: { tone: 'failed', icon: AlertTriangle },
  // Meeting booked (violet, the accent)
  meeting_booked: { tone: 'booked', icon: Calendar },
  booked: { tone: 'booked', icon: Calendar },
  // Callback (amber)
  callback_requested: { tone: 'callback', icon: Clock },
  callback: { tone: 'callback', icon: Clock },
  callback_scheduled: { tone: 'callback', icon: Clock },
  // A live call silent for 10 min: « état inconnu » (ENG-3), amber like a pending action
  unknown: { tone: 'callback', icon: AlertCircle },
  // Do not call (dark red)
  do_not_call: { tone: 'dnc', icon: Ban },
  disabled: { tone: 'dnc', icon: Ban },
};

export function statusEntry(key: string | null | undefined): StatusEntry {
  return (key && STATUS_MAP[key]) || STATUS_MAP.new!;
}
